import { ApolloError } from "@apollo/client";
import { useCallback, useMemo, useRef, useState } from "react";

import {
  useAdvancedCorporationSearchLazyQuery,
  useAutoCreateCustomerGraphMutation,
  useAutoCreatePersonCustomerGraphMutation,
  useCallEReputationMutation,
  useCreateClientByCorporationIdMutation,
  useCreateClientByRaisonSocialeMutation,
  useCreateClientPphByPersonIdMutation,
  useCreateClientPphMutation,
  useFollowFolderMutation,
  useSearchPersonByRegistrationLazyQuery,
} from "src/core/gql/graphql";
import { parseImportCsv } from "src/core/utils/import/parseImportCsv";
import {
  buildCorporationFallbackInput,
  buildPersonFallbackInput,
  processImportRow,
} from "src/core/utils/import/processImportRow";
import {
  AssignmentMode,
  ExternalDataMismatchError,
  ImportRow,
  isExternalDataMismatch,
} from "src/core/utils/import/types";

const IMPORT_CONTEXT = { suppressRedirect: true };

type OperationError = {
  message?: string;
  graphQLErrors?: readonly { message: string }[];
  networkError?: { message?: string } | null;
};

const operationMessages = (result: {
  error?: OperationError;
  errors?: readonly { message: string }[];
}) => {
  const graphQL = result.error?.graphQLErrors?.map((error) => error.message) ?? [];
  const listed = result.errors?.map((error) => error.message) ?? [];
  const network = result.error?.networkError?.message;
  const fallback = result.error?.message;
  return [...listed, ...graphQL, network, fallback].filter((message): message is string =>
    Boolean(message)
  );
};

const throwIfMismatch = (messages: string[]) => {
  const mismatch = messages.find(isExternalDataMismatch);
  if (mismatch) throw new ExternalDataMismatchError(mismatch);
};

const registrationMatches = (value: string | null | undefined, siren: string) => {
  const normalized = (value ?? "").replace(/\s/g, "");
  return normalized.length === 0 || normalized === siren;
};

const assertSearchSucceeded = (result: {
  error?: OperationError;
  errors?: readonly { message: string }[];
  data?: unknown;
}) => {
  const messages = operationMessages(result);
  throwIfMismatch(messages);
  if (!result.data && (result.error || (result.errors?.length ?? 0) > 0)) {
    throw new Error(messages.join(" — ") || "Recherche impossible");
  }
};

export const useImportFolders = () => {
  const [mode, setModeState] = useState<AssignmentMode>("without");
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [fileError, setFileError] = useState<ReturnType<typeof parseImportCsv>["fileError"]>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [phase, setPhase] = useState<"idle" | "running" | "piloted" | "done" | "stopped">("idle");
  const [processed, setProcessed] = useState(0);
  const [planned, setPlanned] = useState(0);

  const contentRef = useRef("");
  const cancelRef = useRef(false);
  const runningRef = useRef(false);
  const processedOrderRef = useRef(0);
  const rowsRef = useRef<ImportRow[]>([]);

  const [searchCorporationQuery] = useAdvancedCorporationSearchLazyQuery();
  const [searchPersonQuery] = useSearchPersonByRegistrationLazyQuery();
  const [autoCreateCorporation] = useAutoCreateCustomerGraphMutation();
  const [autoCreatePerson] = useAutoCreatePersonCustomerGraphMutation();
  const [createByCorporationId] = useCreateClientByCorporationIdMutation();
  const [createByRaisonSociale] = useCreateClientByRaisonSocialeMutation();
  const [createPphByPersonId] = useCreateClientPphByPersonIdMutation();
  const [createPph] = useCreateClientPphMutation();
  const [callEReputationMutation] = useCallEReputationMutation();
  const [followFolderMutation] = useFollowFolderMutation();

  const modeLocked = phase !== "idle";

  const replaceRows = useCallback((next: ImportRow[]) => {
    rowsRef.current = next;
    setRows(next);
  }, []);

  const applyContent = useCallback(
    (content: string, nextMode: AssignmentMode) => {
      const parsed = parseImportCsv(content, nextMode);
      setFileError(parsed.fileError);
      replaceRows(parsed.rows);
      setPhase("idle");
      setProcessed(0);
      setPlanned(0);
      cancelRef.current = false;
      processedOrderRef.current = 0;
    },
    [replaceRows]
  );

  const setMode = useCallback(
    (nextMode: AssignmentMode) => {
      if (modeLocked || nextMode === mode) return;
      setModeState(nextMode);
      if (contentRef.current) applyContent(contentRef.current, nextMode);
    },
    [applyContent, mode, modeLocked]
  );

  const loadFile = useCallback(
    async (file: File) => {
      const content = await file.text();
      contentRef.current = content;
      setFileName(file.name);
      applyContent(content, mode);
    },
    [applyContent, mode]
  );

  const api = useMemo(
    () => ({
      searchCorporation: async (siren: string) => {
        const result = await searchCorporationQuery({
          variables: { values: [siren] },
          errorPolicy: "all",
          fetchPolicy: "no-cache",
          context: IMPORT_CONTEXT,
        });
        assertSearchSucceeded(result);
        const corporation = result.data?.advancedCorporationSearch.corporations.find((item) =>
          registrationMatches(item.siren?.stringValue, siren)
        );
        if (!corporation) return { exists: false, isCustomer: true, corporationId: null };
        return {
          exists: true,
          isCustomer: corporation.isCustomer === true,
          corporationId: corporation.id,
        };
      },
      searchPerson: async (siren: string) => {
        const result = await searchPersonQuery({
          variables: { values: [siren] },
          errorPolicy: "all",
          fetchPolicy: "no-cache",
          context: IMPORT_CONTEXT,
        });
        assertSearchSucceeded(result);
        const person = result.data?.advancedPersonSearch.persons.find((item) =>
          registrationMatches(item.siren?.stringValue, siren)
        );
        return { exists: Boolean(person), personId: person?.id ?? null };
      },
      createAuto: async (siren: string, isPerson: boolean) => {
        if (isPerson) {
          const result = await autoCreatePerson({
            variables: { siren },
            errorPolicy: "all",
            context: IMPORT_CONTEXT,
          });
          throwIfMismatch(operationMessages(result));
          const customerId = result.data?.autoCreatePersonCustomerGraph?.customer?.id ?? null;
          return { success: Boolean(customerId), customerId };
        }

        const result = await autoCreateCorporation({
          variables: { siren, includeIntermediateLegalRepresentatives: true },
          errorPolicy: "all",
          context: IMPORT_CONTEXT,
        });
        throwIfMismatch(operationMessages(result));
        const customerId = result.data?.autoCreateCustomerGraph?.customer?.id ?? null;
        return { success: Boolean(customerId), customerId };
      },
      createFolder: async (input: {
        siren: string;
        entityType: string;
        isPerson: boolean;
        creationAuto: boolean;
        customerId: string | null;
        idOwner: string | null;
      }) => {
        const owner = {
          isOwner: Boolean(input.idOwner),
          ownerId: input.idOwner ?? "",
          isContact: false,
          isFolderFile: true,
        };

        try {
          if (input.isPerson && input.creationAuto && input.customerId) {
            const result = await createPphByPersonId({
              variables: { ...owner, personId: input.customerId },
              errorPolicy: "all",
              context: IMPORT_CONTEXT,
            });
            throwIfMismatch(operationMessages(result));
            return {
              success: Boolean(result.data?.createCustomerFolder?.id),
              customerFolderId: result.data?.createCustomerFolder?.id ?? null,
              folderFileId: result.data?.createFolderFile?.id ?? null,
            };
          }

          if (input.isPerson) {
            const result = await createPph({
              variables: {
                ...owner,
                inputValues: buildPersonFallbackInput(input.siren),
              },
              errorPolicy: "all",
              context: IMPORT_CONTEXT,
            });
            throwIfMismatch(operationMessages(result));
            return {
              success: Boolean(
                result.data?.createPerson?.id && result.data?.createCustomerFolder?.id
              ),
              customerFolderId: result.data?.createCustomerFolder?.id ?? null,
              folderFileId: result.data?.createFolderFile?.id ?? null,
            };
          }

          if (input.creationAuto && input.customerId) {
            const result = await createByCorporationId({
              variables: { ...owner, corporationId: input.customerId },
              errorPolicy: "all",
              context: IMPORT_CONTEXT,
            });
            throwIfMismatch(operationMessages(result));
            return {
              success: Boolean(result.data?.createCustomerFolder?.id),
              customerFolderId: result.data?.createCustomerFolder?.id ?? null,
              folderFileId: result.data?.createFolderFile?.id ?? null,
            };
          }

          const result = await createByRaisonSociale({
            variables: {
              ...owner,
              inputValues: buildCorporationFallbackInput(input.siren, input.entityType),
            },
            errorPolicy: "all",
            context: IMPORT_CONTEXT,
          });
          throwIfMismatch(operationMessages(result));
          return {
            success: Boolean(
              result.data?.createCorporation?.id && result.data?.createCustomerFolder?.id
            ),
            customerFolderId: result.data?.createCustomerFolder?.id ?? null,
            folderFileId: result.data?.createFolderFile?.id ?? null,
          };
        } catch (error) {
          if (error instanceof ExternalDataMismatchError) throw error;
          if (error instanceof ApolloError && isExternalDataMismatch(error.message)) {
            throw new ExternalDataMismatchError(error.message);
          }
          return { success: false, customerFolderId: null, folderFileId: null };
        }
      },
      callEReputation: async (folderFileId: string) => {
        try {
          const result = await callEReputationMutation({
            variables: { folderFileId, force: false },
            errorPolicy: "all",
            context: IMPORT_CONTEXT,
          });
          throwIfMismatch(operationMessages(result));
          return Boolean(result.data?.callEreputation?.id);
        } catch (error) {
          if (error instanceof ExternalDataMismatchError) throw error;
          return false;
        }
      },
      followFolder: async (customerFolderId: string, userId: string) => {
        try {
          const result = await followFolderMutation({
            variables: { customerFolderId, userId },
            errorPolicy: "all",
            context: IMPORT_CONTEXT,
          });
          throwIfMismatch(operationMessages(result));
          return Boolean(result.data?.followFolder?.id);
        } catch (error) {
          if (error instanceof ExternalDataMismatchError) throw error;
          return false;
        }
      },
    }),
    [
      autoCreateCorporation,
      autoCreatePerson,
      callEReputationMutation,
      createByCorporationId,
      createByRaisonSociale,
      createPph,
      createPphByPersonId,
      followFolderMutation,
      searchCorporationQuery,
      searchPersonQuery,
    ]
  );

  const runIndexes = useCallback(
    async (indexes: number[], nextPhase: "piloted" | "done") => {
      if (indexes.length === 0 || runningRef.current) return;
      runningRef.current = true;
      cancelRef.current = false;
      setPhase("running");
      setPlanned(indexes.length);
      setProcessed(0);

      try {
        let done = 0;
        for (const index of indexes) {
          if (cancelRef.current) {
            setPhase("stopped");
            return;
          }

          const current = rowsRef.current[index];
          const outcome = await processImportRow(current, mode, api);
          processedOrderRef.current += 1;
          const stamped = { ...outcome, processedOrder: processedOrderRef.current };
          const next = rowsRef.current.map((row, rowIndex) => (rowIndex === index ? stamped : row));
          replaceRows(next);
          done += 1;
          setProcessed(done);
        }

        const pendingValid = rowsRef.current.some((row) => row.isValid && row.status === "pending");
        setPhase(
          pendingValid && nextPhase === "piloted" ? "piloted" : pendingValid ? "stopped" : "done"
        );
      } finally {
        runningRef.current = false;
      }
    },
    [api, mode, replaceRows]
  );

  const importFirstLine = useCallback(() => {
    const index = rowsRef.current.findIndex((row) => row.isValid && row.status === "pending");
    if (index < 0) return;
    void runIndexes([index], "piloted");
  }, [runIndexes]);

  const importRest = useCallback(() => {
    const indexes = rowsRef.current
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.isValid && row.status === "pending")
      .map(({ index }) => index);
    void runIndexes(indexes, "done");
  }, [runIndexes]);

  const cancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  return {
    mode,
    setMode,
    modeLocked,
    rows,
    fileError,
    fileName,
    phase,
    processed,
    planned,
    loadFile,
    importFirstLine,
    importRest,
    cancel,
  };
};
